// The Summit listing a buyside letter markets, the same for every owner.
// Kept in step with MailMergeService::LISTING_TOKENS.
export const LISTING_TOKENS = [
    { key: 'LISTING_NAME', label: 'Listing' },
    { key: 'LISTING_CITY_STATE', label: 'Listing city, state' },
    { key: 'LISTING_PRICE', label: 'Listing price' },
    { key: 'LISTING_NOI', label: 'Listing NOI' },
    { key: 'LISTING_CAP_RATE', label: 'Listing cap rate' },
    { key: 'LISTING_HIGHLIGHTS', label: 'Listing highlights' },
    { key: 'LISTING_LINK', label: 'Listing link' },
];

// Merge tokens live in the body as contenteditable="false" chips carrying a
// data-token attribute, never as typed {{TEXT}} — half a hand-typed token is
// invisible in a rich editor and would ship a broken email.
export const TOKENS = [
    { key: 'OWNER_NAME', label: 'Owner name' },
    { key: 'OWNER_FIRST_NAME', label: 'First name' },
    { key: 'PROPERTY_LIST', label: 'Property list' },
    { key: 'PROPERTY_NAME', label: 'Property' },
    { key: 'CITY_STATE', label: 'City, State' },
    { key: 'MARKET', label: 'Market' },
    { key: 'AGENT_NAME', label: 'Agent name' },
    { key: 'AGENT_SIGNATURE', label: 'Agent signature' },
].concat(LISTING_TOKENS);

export function isListingToken(key) {
    return LISTING_TOKENS.some((token) => token.key === key);
}

// Kept in step with MailMergeService::MAX_LISTINGS.
export const MAX_LISTINGS = 10;

// The most people one send may reach. Kept in step with MailMergeService::MAX_RECIPIENTS.
export const MAX_RECIPIENTS = 200;

export const AUDIENCE_OWNERS = 'owners';
export const AUDIENCE_LENDERS = 'lenders';
// The investors and brokers who downloaded one property's marketing package.
export const AUDIENCE_DOWNLOADERS = 'downloaders';

// The vendors or agents on the Vendors or Agents list, as the broker filtered it.
export const AUDIENCE_VENDORS = 'vendors';
export const AUDIENCE_AGENTS = 'agents';

// A merge about exactly one property: no property picker, no portfolio modes,
// no listings and no investors added by hand.
export function isSinglePropertyAudience(audience) {
    return audience === AUDIENCE_LENDERS || audience === AUDIENCE_DOWNLOADERS;
}

// A merge picked off a contact list, about no property at all. Kept in step
// with MailMergeService::CONTACT_AUDIENCES.
export function isContactAudience(audience) {
    return audience === AUDIENCE_VENDORS || audience === AUDIENCE_AGENTS;
}

// Kept in step with MailMergeService::LENDER_TOKENS. One property per letter,
// so the property tokens name just that one.
export const LENDER_TOKENS = [
    { key: 'LENDER_CONTACT_NAME', label: 'Contact name' },
    { key: 'LENDER_CONTACT_FIRST_NAME', label: 'First name' },
    { key: 'LENDER_NAME', label: 'Lender' },
    { key: 'PROPERTY_LIST', label: 'Property list' },
    { key: 'PROPERTY_NAME', label: 'Property' },
    { key: 'PROPERTY_ADDRESS', label: 'Property address' },
    { key: 'PURCHASE_PRICE', label: 'Price' },
    { key: 'CAP_RATE', label: 'Cap rate' },
    { key: 'NOI', label: 'NOI' },
    { key: 'HIGHLIGHTS', label: 'Highlights' },
    { key: 'PROPERTY_LINK', label: 'Property link' },
    { key: 'MARKET', label: 'Market' },
    { key: 'AGENT_NAME', label: 'Agent name' },
    { key: 'AGENT_SIGNATURE', label: 'Agent signature' },
];

// Kept in step with MailMergeService::DOWNLOADER_TOKENS, in the order of the
// client's Quick Links. One property, priced the way the website shows it.
export const DOWNLOADER_TOKENS = [
    { key: 'CONTACT_FIRST_NAME', label: 'First name' },
    { key: 'CONTACT_NAME', label: 'Full name' },
    { key: 'PROPERTY_NAME', label: 'Property' },
    { key: 'CITY_STATE', label: 'City, State' },
    { key: 'PURCHASE_PRICE', label: 'Price' },
    { key: 'NOI', label: 'NOI' },
    { key: 'CAP_RATE', label: 'Cap rate' },
    { key: 'PROPERTY_LINK', label: 'Property link' },
    { key: 'PROPERTY_LIST', label: 'Property list' },
    { key: 'HIGHLIGHTS', label: 'Property highlights' },
    { key: 'MARKET', label: 'Market' },
    { key: 'AGENT_NAME', label: 'Agent name' },
    { key: 'AGENT_SIGNATURE', label: 'Agent signature' },
];

// Kept in step with MailMergeService::CONTACT_TOKENS. No property behind the
// letter, so only who it is to and who it is from.
export const CONTACT_TOKENS = [
    { key: 'CONTACT_FIRST_NAME', label: 'First name' },
    { key: 'CONTACT_NAME', label: 'Full name' },
    { key: 'AGENT_NAME', label: 'Agent name' },
    { key: 'AGENT_SIGNATURE', label: 'Agent signature' },
];

export function tokensFor(audience) {
    if (audience === AUDIENCE_LENDERS) return LENDER_TOKENS;
    if (audience === AUDIENCE_DOWNLOADERS) return DOWNLOADER_TOKENS;
    if (isContactAudience(audience)) return CONTACT_TOKENS;
    return TOKENS;
}

/** The heading over a column of the people a merge writes to. */
export function contactHeading(noun) {
    if (noun === 'lender') return 'Lender contact';
    if (noun === 'contact') return 'Contact';
    if (noun === 'vendor') return 'Vendor';
    if (noun === 'agent') return 'Agent';
    return 'Owner';
}

export function tokenLabel(key) {
    const token = TOKENS.concat(LENDER_TOKENS, DOWNLOADER_TOKENS, CONTACT_TOKENS).filter((entry) => entry.key === key)[0];
    return token ? token.label : key;
}

// How much of an owner's portfolio the property tokens name. Kept in step with
// MailMergeService::MODES, which the server validates against.
export const MODE_ALL = 'all';
export const MODE_ONE = 'one';
export const MODE_SUMMARY = 'summary';

// What MODE_SUMMARY says in place of the addresses — the client's own words.
export const SUMMARY_PHRASE = 'multiple properties';

// The client asked for "A, B & C" in the property list; the rest keep the word.
const CONJUNCTION_PROPERTY_LIST = '&';
const CONJUNCTION_DEFAULT = 'and';

function conjunctionFor(key) {
    return key === 'PROPERTY_LIST' ? CONJUNCTION_PROPERTY_LIST : CONJUNCTION_DEFAULT;
}

// What an owner's mail client shows the letter in. Kept identical to
// MailMergeService::FONT_STYLE, and applied the same way — on every element,
// because Outlook renders mail with the Word engine and that resets the font
// across nesting instead of inheriting it.
export const FONT_STYLE = "font-family:Calibri,Carlito,'Segoe UI',Arial,sans-serif; font-size:12pt;";

export const TOKEN_CHIP_STYLE =
    'display:inline-block; padding:0 7px; border-radius:5px; background:#EAF2FC; ' +
    'border:1px solid #CFE0F7; color:#2B4C7E; font-size:12px; font-weight:500; ' +
    'line-height:19px; white-space:nowrap;';

export function tokenChipHtml(key, label) {
    return (
        '<span data-token="' + key + '" contenteditable="false" style="' + TOKEN_CHIP_STYLE + '">' +
        label +
        '</span>'
    );
}

/**
 * The same chip with somewhere to land after it: without the trailing space
 * there is no caret position between a chip and the end of its line. Only for
 * insertion — a chip written into the starting body would render as
 * "Hi Aarash ," once merged.
 */
export function tokenChipInsertHtml(key, label) {
    return tokenChipHtml(key, label) + '&nbsp;';
}

/**
 * The properties this letter speaks about for one owner: the one the broker
 * chose, or all of them. Kept in step with MailMergeRecipient.
 */
export function selectedProperties(recipient, choice) {
    const all = recipient.properties || [];
    if (!choice) return all;
    return all.filter((property) => String(property.id) === String(choice));
}

/**
 * A property split for a picker: the line that tells it apart from the owner's
 * others, and the rest underneath. An owner of five Christian Brothers stores
 * has five identical names, so the address leads.
 */
export function propertyLines(property) {
    const name = (property.name || '').trim();
    const address = (property.address || '').trim();
    const place = [(property.city || '').trim(), (property.state || '').trim()]
        .filter((part) => part)
        .join(', ');

    const primary = address || name || place;
    const rest = [];

    if (place && place !== primary) rest.push(place);
    if (name && name !== primary) rest.push(name);

    return [primary, rest.join('  ·  ')];
}

/** "Wendy's located at 127 Main St in Frisco, TX", with missing parts left out. */
export function propertySentence(property) {
    let sentence = (property.name || '').trim();
    const address = (property.address || '').trim();
    const place = [(property.city || '').trim(), (property.state || '').trim()]
        .filter((part) => part)
        .join(', ');

    if (address) sentence = sentence ? sentence + ' located at ' + address : address;
    if (place) sentence = sentence ? sentence + ' in ' + place : place;

    return sentence;
}

// A link goes out as a clickable anchor, so only a real web address may.
// Kept in step with MailMergeService::safeUrl.
export function safeUrl(url) {
    const text = (url || '').trim();
    return /^https?:\/\/\S+$/i.test(text) ? text : '';
}

/** "Frisco, TX", with whichever part is missing left out. */
export function placeOf(row) {
    return [(row.city || '').trim(), (row.state || '').trim()]
        .filter((part) => part)
        .join(', ');
}

/**
 * What the property link reads as: "Christian Brothers Automotive - Hayden, ID",
 * the way the /links page labels it. Kept in step with
 * MailMergeRecipient::propertyLinkLabel.
 */
export function linkLabel(property) {
    if (!property) return '';
    const name = (property.name || '').trim();
    const location = placeOf(property);

    return name && location ? name + ' - ' + location : name + location;
}

// Price, NOI and cap rate print their own "$" and "%", stored or typed.
// Kept in step with MailMergeRecipient::dollars / percent.
const dollars = (value) => '$' + value.replace(/^[$\s]+/, '');
const percent = (value) => value.replace(/[%\s]+$/, '') + '%';

/**
 * A "$" or "%" the broker typed beside a price, NOI or cap rate chip, as the
 * earlier starter letter had, is dropped rather than doubled. Kept in step
 * with MailMergeService::absorbTypedSymbol.
 */
function absorbTypedSymbol(chip, key) {
    const before = chip.previousSibling;
    const after = chip.nextSibling;

    const dollarKeys = ['PURCHASE_PRICE', 'NOI', 'LISTING_PRICE', 'LISTING_NOI'];

    if (dollarKeys.indexOf(key) !== -1 && before && before.nodeType === 3 && /\$\s*$/.test(before.data)) {
        before.data = before.data.replace(/\$\s*$/, '');
    }
    if ((key === 'CAP_RATE' || key === 'LISTING_CAP_RATE') && after && after.nodeType === 3 && /^\s*%/.test(after.data)) {
        after.data = after.data.replace(/^\s*%/, '');
    }
}

/**
 * The lines in place of the chip, one per line, broken off from any text
 * around them. Kept in step with MailMergeService::replaceWithBrokenLines.
 */
function replaceWithBrokenLines(chip, lines) {
    const parent = chip.parentNode;
    const hasText = (node) => Boolean(node) && bare(node.textContent) !== '';

    if (lines.length && hasText(chip.previousSibling)) parent.insertBefore(document.createElement('br'), chip);
    lines.forEach((line, i) => {
        if (i > 0) parent.insertBefore(document.createElement('br'), chip);
        parent.insertBefore(document.createTextNode(line), chip);
    });
    if (lines.length && hasText(chip.nextSibling)) parent.insertBefore(document.createElement('br'), chip);
    parent.removeChild(chip);
}

/**
 * One anchor per listing, each on a line of its own. Kept in step with
 * MailMergeService::replaceWithLinks.
 */
function replaceWithLinks(chip, links) {
    const parent = chip.parentNode;

    links.forEach((link, i) => {
        if (i > 0) parent.insertBefore(document.createElement('br'), chip);

        const anchor = document.createElement('a');
        anchor.setAttribute('href', link.url);
        anchor.setAttribute('target', '_blank');
        anchor.textContent = link.label || link.url;
        parent.insertBefore(anchor, chip);
    });

    parent.removeChild(chip);
}

/** "6191 Sawcut Point in Fountain, CO", with missing parts left out. */
function addressInPlace(property) {
    const address = (property.address || '').trim();
    const place = [(property.city || '').trim(), (property.state || '').trim()]
        .filter((part) => part)
        .join(', ');

    return address && place ? address + ' in ' + place : address + place;
}

// The field holds one bullet per line, and sometimes a literal backslash-n
// where a newline was meant. Kept in step with MailMergeRecipient::bulletLines.
function bulletLines(texts) {
    const lines = [];

    texts.forEach((text) => {
        (text || '').split(/(?:\\r)?\\n|\r\n|\r|\n/).forEach((line) => {
            const bare = line.replace(/^[\s\u2022*-]+/, '').trim();
            if (bare) lines.push('\u2022 ' + bare);
        });
    });

    return lines;
}

// The highlights as bulleted lines.
function highlightLines(recipient, options, override) {
    const typed = (override || '').trim();

    return bulletLines(typed
        ? [typed]
        : selectedProperties(recipient, options.choice).map((property) => property.highlights || ''));
}

/** Each value once, in order, or just the one typed in their place. */
function distinctValues(values, override) {
    const typed = (override || '').trim();
    if (typed) return [typed];

    const seen = {};

    return values
        .map((value) => (value || '').trim())
        .filter((value) => {
            if (!value || seen[value]) return false;
            seen[value] = true;
            return true;
        });
}

/**
 * One link per listing that has one, or the one typed in where none had any.
 * Kept in step with MailMergeRecipient::listingLinks.
 */
export function listingLinks(listings, listingOverrides) {
    const typed = safeUrl((listingOverrides || {}).LISTING_LINK);
    if (typed) return [{ url: typed, label: listings.length === 1 ? linkLabel(listings[0]) : '' }];

    const seen = {};

    return listings
        .map((listing) => ({ url: safeUrl(listing.link), label: linkLabel(listing) }))
        .filter((link) => {
            if (!link.url || seen[link.url]) return false;
            seen[link.url] = true;
            return true;
        });
}

// The listing reads the same for every owner, so it comes from the options
// rather than the recipient. Kept in step with MailMergeRecipient's listing lines.
function listingLines(key, options) {
    const listings = options.listings || [];
    const typed = options.listingOverrides || {};
    const field = (name) => distinctValues(listings.map((listing) => listing[name]), typed[key]);

    if (key === 'LISTING_NAME') return field('name');
    if (key === 'LISTING_CITY_STATE') return distinctValues(listings.map(placeOf), typed[key]);
    if (key === 'LISTING_PRICE') return field('price').map(dollars);
    if (key === 'LISTING_NOI') return field('noi').map(dollars);
    if (key === 'LISTING_CAP_RATE') return field('cap_rate').map(percent);
    if (key === 'LISTING_HIGHLIGHTS') {
        const own = (typed[key] || '').trim();
        return bulletLines(own ? [own] : listings.map((listing) => listing.highlights || ''));
    }

    return listingLinks(listings, typed).map((link) => link.url);
}

function distinctField(recipient, options, field, override, clean) {
    const tidy = clean || ((value) => (value || '').trim());
    if (override && tidy(override)) return [tidy(override)];

    const seen = {};
    const values = [];

    selectedProperties(recipient, options.choice).forEach((property) => {
        const value = tidy(property[field]);
        if (value && !seen[value]) {
            seen[value] = true;
            values.push(value);
        }
    });

    return values;
}

/**
 * Name, company, phone, email — the four lines the client wrote onto the
 * preview. Kept in step with MailMergeService::signatureLines.
 */
export function signatureLines(sender) {
    return [
        sender.name,
        sender.company,
        sender.phone ? 'P: ' + sender.phone : '',
        sender.email ? 'E: ' + sender.email : '',
    ].filter((line) => line);
}

function linesFor(key, recipient, sender, options) {
    const overrides = options.overrides || {};

    if (isListingToken(key)) return listingLines(key, options);
    if (key === 'CITY_STATE') {
        return distinctValues(selectedProperties(recipient, options.choice).map(placeOf), overrides.CITY_STATE);
    }
    if (key === 'PROPERTY_LIST') {
        const properties = selectedProperties(recipient, options.choice);

        // "Multiple properties" about one property would simply be untrue.
        if (options.mode === MODE_SUMMARY && properties.length > 1) return [SUMMARY_PHRASE];

        return properties.map(propertySentence).filter((line) => line);
    }
    if (key === 'PROPERTY_NAME') return distinctField(recipient, options, 'name', overrides.PROPERTY_NAME);
    if (key === 'MARKET') return distinctField(recipient, options, 'market', overrides.MARKET);
    if (key === 'PROPERTY_ADDRESS') {
        const typed = (overrides.PROPERTY_ADDRESS || '').trim();
        if (typed) return [typed];
        return selectedProperties(recipient, options.choice).map(addressInPlace).filter((line) => line);
    }
    if (key === 'PURCHASE_PRICE') return distinctField(recipient, options, 'price', overrides.PURCHASE_PRICE).map(dollars);
    if (key === 'CAP_RATE') return distinctField(recipient, options, 'cap_rate', overrides.CAP_RATE).map(percent);
    if (key === 'NOI') return distinctField(recipient, options, 'noi', overrides.NOI).map(dollars);
    if (key === 'HIGHLIGHTS') return highlightLines(recipient, options, overrides.HIGHLIGHTS);
    if (key === 'PROPERTY_LINK') return distinctField(recipient, options, 'link', overrides.PROPERTY_LINK, safeUrl);
    if (key === 'AGENT_SIGNATURE') return signatureLines(sender);
    return null;
}

function plainValue(key, recipient, sender) {
    if (key === 'OWNER_NAME' || key === 'LENDER_CONTACT_NAME' || key === 'CONTACT_NAME') return recipient.name || '';
    if (key === 'OWNER_FIRST_NAME' || key === 'LENDER_CONTACT_FIRST_NAME' || key === 'CONTACT_FIRST_NAME') return recipient.first_name || '';
    if (key === 'LENDER_NAME') return recipient.lender || '';
    return sender.name || '';
}

/**
 * "A", "A & B", "A, B & C". Joining every entry with a comma reads as one
 * run-on list, because each entry already carries the comma before its state.
 */
function inline(lines, conjunction) {
    const kept = lines.filter((line) => line);
    if (kept.length < 2) return kept[0] || '';
    return kept.slice(0, -1).join(', ') + ' ' + (conjunction || CONJUNCTION_DEFAULT) + ' ' + kept[kept.length - 1];
}

function block(lines) {
    const holder = document.createElement('div');
    holder.setAttribute('style', 'margin:6px 0;');

    lines.forEach((line) => {
        const row = document.createElement('div');
        row.setAttribute('style', 'line-height:1.7;');
        row.textContent = line;
        holder.appendChild(row);
    });

    return holder;
}

// Inserting a token leaves a non-breaking space after the chip so the caret
// has somewhere to go, and trim() does not touch it.
function bare(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
}

/** Every token key written into a piece of markup, in document order. */
export function tokensIn(html) {
    const holder = document.createElement('div');
    holder.innerHTML = html || '';

    return Array.prototype.map.call(
        holder.querySelectorAll('[data-token]'),
        (chip) => chip.getAttribute('data-token'),
    );
}

/**
 * One typeface, on every element. Mirrors MailMergeService::applyFont, down to
 * dropping whatever font came in with pasted content — that is where the
 * client's Aptos came from.
 */
function applyFont(root) {
    // Text the editor left outside any element has nothing to carry the font,
    // so it gets a span of its own.
    Array.prototype.slice.call(root.childNodes).forEach((node) => {
        if (node.nodeType !== 3 || !node.nodeValue.trim()) return;

        const span = document.createElement('span');
        root.replaceChild(span, node);
        span.appendChild(node);
    });

    Array.prototype.forEach.call(root.querySelectorAll('*'), (element) => {
        if (['BR', 'IMG', 'HR'].indexOf(element.nodeName) !== -1) return;

        const kept = (element.getAttribute('style') || '')
            .split(';')
            .map((rule) => rule.trim())
            .filter((rule) => rule && !/^font(-family|-size)?\s*:/i.test(rule));

        element.setAttribute('style', FONT_STYLE + ' ' + kept.join('; '));
    });
}

/**
 * The body as one recipient will read it.
 *
 * Kept in step with MailMergeService::render on the server, which does the
 * same substitution over the same chips at send time — this one only feeds the
 * preview.
 */
export function substituteTokens(bodyHtml, recipient, sender, options) {
    const settings = options || {};
    const holder = document.createElement('div');
    holder.innerHTML = bodyHtml || '';

    Array.prototype.forEach.call(holder.querySelectorAll('[data-token]'), (chip) => {
        const key = chip.getAttribute('data-token');
        const lines = linesFor(key, recipient, sender, settings);

        // The link tokens are the ones that are markup: a clickable link, or nothing.
        if (key === 'LISTING_LINK') {
            replaceWithLinks(chip, listingLinks(settings.listings || [], settings.listingOverrides));
            return;
        }

        if (key === 'PROPERTY_LINK') {
            const url = lines[0] || '';
            let node = document.createTextNode('');

            if (url) {
                node = document.createElement('a');
                node.setAttribute('href', url);
                node.setAttribute('target', '_blank');
                node.textContent = linkLabel(selectedProperties(recipient, settings.choice)[0]) || url;
            }

            chip.parentNode.replaceChild(node, chip);
            return;
        }

        if (lines && lines.length) absorbTypedSymbol(chip, key);

        if (lines) {
            // A block only where the chip has a paragraph to itself. Written
            // mid-sentence it reads inline, or the sentence around it is split
            // into pieces on screen. The property list is one sentence either
            // way — the client asked for that outright in 553001.
            const parent = chip.parentNode;
            const alone =
                key !== 'PROPERTY_LIST' &&
                parent &&
                parent.nodeName.toLowerCase() === 'p' &&
                bare(parent.textContent) === bare(chip.textContent);

            if (alone) parent.parentNode.replaceChild(block(lines), parent);
            // Highlights are bullets: each keeps a line of its own even mid-sentence.
            else if (key === 'HIGHLIGHTS' || key === 'LISTING_HIGHLIGHTS') replaceWithBrokenLines(chip, lines);
            else parent.replaceChild(document.createTextNode(inline(lines, conjunctionFor(key))), chip);
            return;
        }

        chip.parentNode.replaceChild(document.createTextNode(plainValue(key, recipient, sender)), chip);
    });

    applyFont(holder);

    return '<div style="' + FONT_STYLE + '">' + holder.innerHTML + '</div>';
}

/**
 * A subject line for one recipient: the same chips, flattened to the plain
 * text a subject has to be. A property list reads inline here — a block of
 * addresses in a subject line is nonsense.
 *
 * Kept in step with MailMergeService::renderSubject, which does this again at
 * send time.
 */
export function substituteSubject(subjectHtml, recipient, sender, options) {
    const settings = options || {};
    const holder = document.createElement('div');
    holder.innerHTML = subjectHtml || '';

    Array.prototype.forEach.call(holder.querySelectorAll('[data-token]'), (chip) => {
        const key = chip.getAttribute('data-token');
        const lines = linesFor(key, recipient, sender, settings);
        const value = lines ? inline(lines, conjunctionFor(key)) : plainValue(key, recipient, sender);

        chip.parentNode.replaceChild(document.createTextNode(value || ''), chip);
    });

    return holder.textContent.replace(/\s+/g, ' ').trim();
}

/**
 * Of the tokens this letter uses, the ones that would reach this owner empty.
 *
 * The whole point is that nobody sends "I know you own  and wonder…", so the
 * test is simply whether the token renders to nothing at all — not whether
 * every one of an owner's properties has the field.
 *
 * Takes the token keys rather than the markup: they are the same for every
 * recipient, and re-parsing the draft once per owner on every keystroke is
 * work the page does not need to do.
 */
export function blankTokensFor(keys, recipient, sender, options) {
    const settings = options || {};

    return keys.filter((key) => {
        // Blank for one owner means blank for all of them, so it is asked once, beside the listing.
        if (isListingToken(key)) return false;

        const lines = linesFor(key, recipient, sender, settings);
        return lines ? inline(lines) === '' : plainValue(key, recipient, sender).trim() === '';
    });
}

/** Of the listing tokens this letter uses, the ones the picked listings leave blank. */
export function blankListingTokens(keys, listings, listingOverrides) {
    const options = { listings, listingOverrides };

    return keys.filter((key) => isListingToken(key) && inline(listingLines(key, options)) === '');
}

/** The distinct token keys a subject and body use together. */
export function tokenKeysUsed(subjectHtml, bodyHtml) {
    const seen = {};

    tokensIn(subjectHtml).concat(tokensIn(bodyHtml)).forEach((key) => { seen[key] = true; });

    return Object.keys(seen);
}

/** The subject as written, chips reading as their labels. */
export function subjectTemplateText(subjectHtml) {
    const holder = document.createElement('div');
    holder.innerHTML = subjectHtml || '';
    return holder.textContent.replace(/\s+/g, ' ').trim();
}

/**
 * Chips describing where the list came from, built from the list's own query
 * string. Nothing is invented: a filter the URL does not carry is not shown.
 */
export function filterChips(query) {
    const skip = { page: true, order: true, limit: true, offset: true };

    return Object.keys(query || {})
        .filter((key) => !skip[key] && query[key] !== '' && query[key] != null)
        .map((key) => {
            const value = String(query[key]);
            const isFlag = value === 'true' || value === 'y' || value === '1';

            return {
                key,
                label: key.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()),
                value: isFlag ? null : value,
            };
        });
}

// What the server accepts as an attachment, and the most it will take in all:
// Graph refuses a request of 4 MB, so the files and the letter share that.
// Keep in step with MailMergeService::MAX_ATTACHMENT_BYTES.
export const MAX_ATTACHMENT_BYTES = 2900000;
export const MAX_ATTACHMENTS = 10;
export const ATTACHMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'csv'];

export function isAttachableName(name) {
    const dot = name.lastIndexOf('.');
    return dot !== -1 && ATTACHMENT_EXTENSIONS.indexOf(name.slice(dot + 1).toLowerCase()) !== -1;
}

export function humanSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
